import {
  ApiAIPromptStatus,
  ApiReadLanderResponse,
  ApiSimpleComponent,
  ApiPathStatus,
  ApiUTMEntityLevel,
  ApiExternalEntityStatus,
  ApiClassificationItem,
  ApiSimpleCampaign,
} from "@incendium/api";
import {
  Box,
  Button,
  ButtonGroup,
  Chip,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import { LinkOff, OpenInNew, Preview } from "@mui/icons-material";
import { Link } from "react-router-dom";
import SmartLinkSelector, { SMART_LINK_TYPE_ICONS } from "./SmartLinkSelector";
import { truncate } from "Helpers/truncate";
import StatusLabel from "Components/UI/StatusLabel";
import TraitsMultiSelect from "features/audiences/components/TraitsMultiSelect";
import CustomDialog from "Components/CustomDialog/CustomDialog";
import { useState, useMemo } from "react";
import { useClassifications } from "Hooks";
import { useComponents } from "Hooks/useComponents";
import useProtectedRoutes from "Hooks/useProtectedRoutes";
import { useSelectedProject } from "Hooks";
import { RouteNames } from "Hooks/routeNames";
import { appendTrailingSlash } from "Helpers";

export type SmartLinkEntityType = "campaign" | "adGroup" | "ad";

export interface SmartLinkEntity {
  id: number;
  name: string;
  type: SmartLinkEntityType;
  level: ApiUTMEntityLevel;
  campaignName?: string;
  adGroupName?: string;
  entityStatus?: ApiExternalEntityStatus;
}

export interface EntityLinkState {
  linkType: "lander" | "url" | "campaign";
  selectedLanderId: number | "";
  customUrl: string;
  selectedCampaignId: number | "";
  selectedClassificationItems: ApiClassificationItem[];
}

interface LanderInfo {
  path: string;
  url: string;
  componentId?: number;
  status?: string;
  component?: ApiSimpleComponent | null;
  isUnderConstruction?: boolean;
}

interface SmartLinkCellProps {
  entity: SmartLinkEntity;
  landingPathId?: number;
  smartLinkUrl?: string;
  splitCampaignId?: number;
  state: EntityLinkState;
  isSaving: boolean;
  landers: ApiReadLanderResponse[];
  campaigns: ApiSimpleCampaign[];
  onStateChange: (key: string, updates: Partial<EntityLinkState>) => void;
  onSave: (
    entity: SmartLinkEntity,
    linkType: "lander" | "url" | "campaign",
    value: number | string | null,
    clearValue?: string | null,
    classificationItemIds?: number[],
  ) => void;
  onEditLander?: (componentId: number) => void;
  onPreviewComponent?: (component: ApiSimpleComponent) => void;
  inheritedFrom?: "direct" | "adGroup" | "campaign" | "none";
  isPending?: boolean;
  showStatus?: boolean;
}

function renderStatus(
  isPending: boolean,
  isInherited: boolean,
  landerInfo: LanderInfo | null,
) {
  if (isPending) {
    return (
      <StatusLabel
        variant="body2"
        value="creating"
        mapping={{
          creating: "warning.main",
          under_construction: "warning.main",
          [ApiPathStatus.PATH_STATUS_NONE]: "secondary",
          [ApiPathStatus.PATH_STATUS_INACTIVE]: "secondary",
          [ApiPathStatus.PATH_STATUS_ACTIVE]: "success.main",
          [ApiPathStatus.PATH_STATUS_DELETING]: "error.main",
        }}
      >
        Creating...
      </StatusLabel>
    );
  }

  if (!isInherited && landerInfo) {
    return (
      <StatusLabel
        variant="body2"
        value={
          landerInfo.isUnderConstruction
            ? "under_construction"
            : `${landerInfo.status || ApiPathStatus.PATH_STATUS_NONE}`
        }
        mapping={{
          under_construction: "warning.main",
          [ApiPathStatus.PATH_STATUS_NONE]: "secondary",
          [ApiPathStatus.PATH_STATUS_INACTIVE]: "secondary",
          [ApiPathStatus.PATH_STATUS_ACTIVE]: "success.main",
          [ApiPathStatus.PATH_STATUS_DELETING]: "error.main",
        }}
      >
        {landerInfo.isUnderConstruction
          ? "Under Construction"
          : landerInfo.status
            ? `Created - ${landerInfo.status.replace("PathStatus", "")}`
            : "Inactive"}
      </StatusLabel>
    );
  }

  return null;
}

export function SmartLinkCell({
  entity,
  landingPathId,
  smartLinkUrl,
  splitCampaignId,
  state,
  isSaving,
  landers,
  campaigns,
  onStateChange,
  onSave,
  onEditLander,
  onPreviewComponent,
  inheritedFrom = "direct",
  isPending = false,
  showStatus = false,
}: SmartLinkCellProps) {
  const [traitsDialogOpen, setTraitsDialogOpen] = useState(false);
  const { classifications } = useClassifications();
  const { components } = useComponents();

  const entityKey = useMemo(
    () => `${entity.type}-${entity.id}`,
    [entity.type, entity.id],
  );
  const hasLander = useMemo(() => !!landingPathId, [landingPathId]);
  const hasUrl = useMemo(() => !!smartLinkUrl, [smartLinkUrl]);
  const hasCampaign = useMemo(() => !!splitCampaignId, [splitCampaignId]);
  const hasLink = hasLander || hasUrl || hasCampaign;
  const isInherited = inheritedFrom !== "direct" && inheritedFrom !== "none";

  const splitCampaign = useMemo(
    () =>
      splitCampaignId
        ? campaigns.find((c) => c.id === splitCampaignId)
        : undefined,
    [splitCampaignId, campaigns],
  );

  const landerMap = useMemo(
    () => new Map(landers.map((l) => [l.id, l])),
    [landers],
  );
  const componentMap = useMemo(
    () => new Map(components.map((c) => [c.id, c])),
    [components],
  );

  const landerInfo = useMemo((): LanderInfo | null => {
    if (!landingPathId) return null;
    const lander = landerMap.get(landingPathId);
    if (!lander) return null;
    const component = lander.componentId
      ? componentMap.get(lander.componentId)
      : undefined;
    return {
      path: lander.path || component?.name || "",
      url: appendTrailingSlash(lander.url || ""),
      componentId: lander.componentId,
      status: lander.status,
      component: component || null,
      isUnderConstruction:
        component?.prompt?.status ===
        ApiAIPromptStatus.AI_PROMPT_STATUS_PENDING,
    };
  }, [landingPathId, landerMap, componentMap]);

  const selectedClassificationItems = useMemo(
    () => state.selectedClassificationItems || [],
    [state.selectedClassificationItems],
  );
  const traitCount = useMemo(
    () => selectedClassificationItems.length,
    [selectedClassificationItems],
  );
  const classificationItemIds = useMemo(
    () => selectedClassificationItems.map((ci) => ci.id as number),
    [selectedClassificationItems],
  );
  const landerId = useMemo(
    () => (state.selectedLanderId ? (state.selectedLanderId as number) : null),
    [state.selectedLanderId],
  );
  const customUrlValue = useMemo(
    () => state.customUrl || "",
    [state.customUrl],
  );
  const campaignIdValue = useMemo(
    () =>
      state.selectedCampaignId ? (state.selectedCampaignId as number) : null,
    [state.selectedCampaignId],
  );

  const getTraitLabel = useMemo(
    () => (item: ApiClassificationItem) => {
      const classification = classifications.find(
        (c) => c.id === item.classificationId,
      );
      const classificationName = classification?.name || "Unknown";
      return `${classificationName}: ${item.value || ""}`;
    },
    [classifications],
  );

  const LinkTypeIcon = hasLander
    ? SMART_LINK_TYPE_ICONS.lander
    : hasUrl
      ? SMART_LINK_TYPE_ICONS.url
      : SMART_LINK_TYPE_ICONS.campaign;
  const linkTypeLabel = hasLander
    ? "Lightning Lander"
    : hasUrl
      ? "Custom URL"
      : "Split Campaign";

  const { pathByName } = useProtectedRoutes();
  const { selectedProject } = useSelectedProject();
  const conversionLabSetupUrl = useMemo(() => {
    if (
      !splitCampaign?.id ||
      !selectedProject?.clientId ||
      !selectedProject?.id
    )
      return null;
    return `${pathByName(RouteNames.AB_TESTING_LAB_PUBLISH, { clientId: selectedProject.clientId, projectId: selectedProject.id })}?setup=${splitCampaign.id}`;
  }, [
    pathByName,
    selectedProject?.clientId,
    selectedProject?.id,
    splitCampaign?.id,
  ]);

  if (hasLink && !isInherited) {
    // Direct link - show with remove button
    return (
      <Stack py={1} direction="row" spacing={1} alignItems="center">
        {hasLander && landerInfo ? (
          <Stack
            direction="row"
            spacing={2}
            alignItems="center"
            sx={{ flex: 1, minWidth: 0 }}
          >
            <Tooltip title={linkTypeLabel} arrow>
              <Box
                sx={{ display: "flex", color: "text.secondary", flexShrink: 0 }}
              >
                <LinkTypeIcon sx={{ fontSize: 20 }} />
              </Box>
            </Tooltip>
            <Stack
              direction="column"
              spacing={0.5}
              sx={{ flex: 1, minWidth: 0 }}
            >
              {landerInfo.component && landerInfo.componentId && (
                <Stack direction="row" spacing={1} alignItems="center">
                  {onEditLander && (
                    <Tooltip title="Edit" arrow>
                      <Button
                        disabled={
                          landerInfo.component.prompt?.status ===
                          ApiAIPromptStatus.AI_PROMPT_STATUS_PENDING
                        }
                        startIcon={<OpenInNew fontSize="extraSmall" />}
                        variant="text"
                        color="inherit"
                        size="extraSmall"
                        onClick={() =>
                          onEditLander(landerInfo.componentId as number)
                        }
                        sx={{ textTransform: "none", p: 0, minWidth: "auto" }}
                      >
                        {landerInfo.path}
                      </Button>
                    </Tooltip>
                  )}
                </Stack>
              )}
              {landerInfo.url && (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ flex: 1 }}
                >
                  {landerInfo.url}
                </Typography>
              )}
            </Stack>
          </Stack>
        ) : hasUrl && smartLinkUrl ? (
          <Stack
            direction="row"
            spacing={2}
            alignItems="center"
            sx={{ flex: 1, minWidth: 0 }}
          >
            <Tooltip title={linkTypeLabel} arrow>
              <Box
                sx={{ display: "flex", color: "text.secondary", flexShrink: 0 }}
              >
                <LinkTypeIcon sx={{ fontSize: 20 }} />
              </Box>
            </Tooltip>
            <Typography variant="body2" color="text.secondary" sx={{ flex: 1 }}>
              {truncate(smartLinkUrl, 100)}
            </Typography>
            {onPreviewComponent && landerInfo?.component && (
              <ButtonGroup size="small" variant="outlined">
                <Tooltip title="Preview" arrow>
                  <IconButton
                    size="small"
                    onClick={() => onPreviewComponent(landerInfo.component!)}
                    disabled={
                      landerInfo.component.prompt?.status ===
                      ApiAIPromptStatus.AI_PROMPT_STATUS_PENDING
                    }
                  >
                    <Preview fontSize="small" />
                  </IconButton>
                </Tooltip>
              </ButtonGroup>
            )}
          </Stack>
        ) : hasCampaign && splitCampaign ? (
          <Stack
            direction="row"
            spacing={2}
            alignItems="center"
            sx={{ flex: 1, minWidth: 0 }}
          >
            <Tooltip title={linkTypeLabel} arrow>
              <Box
                sx={{ display: "flex", color: "text.secondary", flexShrink: 0 }}
              >
                <LinkTypeIcon sx={{ fontSize: 20 }} />
              </Box>
            </Tooltip>
            <Stack
              direction="column"
              alignItems="flex-start"
              spacing={0.5}
              sx={{ flex: 1, minWidth: 0 }}
            >
              {conversionLabSetupUrl ? (
                <Tooltip title="Edit campaign" arrow>
                  <Button
                    component={Link}
                    to={conversionLabSetupUrl}
                    startIcon={<OpenInNew fontSize="extraSmall" />}
                    variant="text"
                    color="inherit"
                    size="extraSmall"
                    sx={{ textTransform: "none", p: 0, minWidth: "auto" }}
                  >
                    {splitCampaign.name || `Campaign ${splitCampaignId}`}
                  </Button>
                </Tooltip>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  {splitCampaign.name || `Campaign ${splitCampaignId}`}
                </Typography>
              )}
              {splitCampaign.url && (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ flex: 1 }}
                >
                  {splitCampaign.url}
                </Typography>
              )}
            </Stack>
          </Stack>
        ) : null}
        <Stack justifyContent="flex-end" alignItems="flex-end">
          <ButtonGroup size="small" variant="outlined">
            <Tooltip
              arrow
              title={
                traitCount > 0 ? (
                  <Stack spacing={0.5}>
                    <Typography
                      variant="body2"
                      sx={{ fontWeight: "bold" }}
                      color="inherit"
                    >
                      Applied Traits ({traitCount}):
                    </Typography>
                    {selectedClassificationItems.map((item) => (
                      <Typography key={item.id} variant="body2" color="inherit">
                        • {getTraitLabel(item)}
                      </Typography>
                    ))}
                  </Stack>
                ) : (
                  "Manage traits"
                )
              }
            >
              <Button
                size="extraSmall"
                variant="text"
                onClick={() => setTraitsDialogOpen(true)}
                disabled={
                  isSaving ||
                  landerInfo?.component?.prompt?.status ===
                    ApiAIPromptStatus.AI_PROMPT_STATUS_PENDING
                }
                color="secondary"
              >
                Manage Traits {traitCount > 0 ? `(${traitCount})` : ""}
              </Button>
            </Tooltip>
            {onPreviewComponent && landerInfo?.component && (
              <Tooltip title="Preview" arrow>
                <IconButton
                  size="small"
                  onClick={() => onPreviewComponent(landerInfo.component!)}
                  disabled={
                    landerInfo.component.prompt?.status ===
                    ApiAIPromptStatus.AI_PROMPT_STATUS_PENDING
                  }
                >
                  <Preview fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            <Tooltip title="Remove link" arrow>
              <IconButton
                size="small"
                color="error"
                onClick={() => {
                  if (hasLander) {
                    onSave(entity, "lander", null, "", []);
                  } else if (hasCampaign) {
                    onSave(entity, "campaign", null, "", []);
                  } else {
                    onSave(entity, "url", "", null, []);
                  }
                }}
                disabled={
                  isSaving ||
                  landerInfo?.component?.prompt?.status ===
                    ApiAIPromptStatus.AI_PROMPT_STATUS_PENDING
                }
              >
                <LinkOff fontSize="small" />
              </IconButton>
            </Tooltip>
          </ButtonGroup>
          {showStatus && renderStatus(isPending, isInherited, landerInfo)}
        </Stack>
        <CustomDialog
          open={traitsDialogOpen}
          onClose={() => setTraitsDialogOpen(false)}
          fullWidth
          maxWidth="sm"
        >
          <DialogTitle>Manage Traits</DialogTitle>
          <DialogContent>
            <Box sx={{ mt: 2 }}>
              <TraitsMultiSelect
                label="Traits"
                helperText="Select which audience traits to apply to this smart link"
                size="small"
                fullWidth
                variant="outlined"
                values={selectedClassificationItems}
                onChange={(items) => {
                  onStateChange(entityKey, {
                    selectedClassificationItems: items || [],
                  });
                }}
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button
              color="secondary"
              onClick={() => setTraitsDialogOpen(false)}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={() => {
                if (hasLander) {
                  onSave(entity, "lander", landerId, "", classificationItemIds);
                } else if (hasCampaign) {
                  onSave(
                    entity,
                    "campaign",
                    splitCampaignId || null,
                    "",
                    classificationItemIds,
                  );
                } else if (hasUrl) {
                  onSave(
                    entity,
                    "url",
                    smartLinkUrl || "",
                    null,
                    classificationItemIds,
                  );
                }
                setTraitsDialogOpen(false);
              }}
              disabled={isSaving}
            >
              Save
            </Button>
          </DialogActions>
        </CustomDialog>
      </Stack>
    );
  }

  if (hasLink && isInherited) {
    return (
      <Stack spacing={2} py={1}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <SmartLinkSelector
            linkType={state.linkType}
            onLinkTypeChange={(type) => {
              onStateChange(entityKey, { linkType: type });
            }}
            selectedLanderId={state.selectedLanderId}
            onLanderChange={(id) => {
              onStateChange(entityKey, { selectedLanderId: id });
            }}
            customUrl={state.customUrl}
            onCustomUrlChange={(url) => {
              onStateChange(entityKey, { customUrl: url });
            }}
            landers={landers}
            campaigns={campaigns}
            selectedCampaignId={state.selectedCampaignId}
            onCampaignChange={(id) => {
              onStateChange(entityKey, { selectedCampaignId: id });
            }}
            disabled={isSaving}
          />
          <Button
            variant="contained"
            size="small"
            onClick={() => {
              if (state.linkType === "lander") {
                onSave(
                  entity,
                  "lander",
                  landerId as any,
                  "",
                  classificationItemIds,
                );
              } else if (state.linkType === "campaign") {
                onSave(
                  entity,
                  "campaign",
                  campaignIdValue as any,
                  "",
                  classificationItemIds,
                );
              } else {
                onSave(
                  entity,
                  "url",
                  customUrlValue,
                  null,
                  classificationItemIds,
                );
              }
            }}
            disabled={isSaving}
          >
            {isSaving ? "Saving..." : "Save"}
          </Button>
        </Box>
        <Stack direction="row" spacing={1} alignItems="center">
          <Chip
            label={
              inheritedFrom === "adGroup" ? "From Ad Set" : "From Campaign"
            }
            size="small"
            variant="outlined"
            sx={{ fontSize: "0.7rem", height: 20 }}
          />
          <Tooltip title={linkTypeLabel} arrow>
            <Box
              sx={{ display: "flex", color: "text.secondary", flexShrink: 0 }}
            >
              <LinkTypeIcon sx={{ fontSize: 20 }} />
            </Box>
          </Tooltip>
          {hasLander && landerInfo ? (
            <Stack direction="column" spacing={0.5} sx={{ flex: 1 }}>
              {landerInfo.component &&
                landerInfo.componentId &&
                onEditLander && (
                  <Tooltip title="Edit" arrow>
                    <Button
                      disabled={
                        landerInfo.component.prompt?.status ===
                        ApiAIPromptStatus.AI_PROMPT_STATUS_PENDING
                      }
                      startIcon={<OpenInNew fontSize="extraSmall" />}
                      variant="text"
                      color="inherit"
                      size="extraSmall"
                      onClick={() =>
                        onEditLander(landerInfo.componentId as number)
                      }
                      sx={{ textTransform: "none", p: 0, minWidth: "auto" }}
                    >
                      {landerInfo.path}
                    </Button>
                  </Tooltip>
                )}
              {landerInfo.url && (
                <Typography variant="body2" color="text.secondary">
                  {landerInfo.url}
                </Typography>
              )}
            </Stack>
          ) : hasUrl && smartLinkUrl ? (
            <Typography variant="body2" color="text.secondary" sx={{ flex: 1 }}>
              {truncate(smartLinkUrl || "", 100)}
            </Typography>
          ) : hasCampaign && splitCampaign ? (
            <Stack direction="column" spacing={0.5} sx={{ flex: 1 }}>
              {conversionLabSetupUrl ? (
                <Tooltip title="Edit campaign" arrow>
                  <Button
                    component={Link}
                    to={conversionLabSetupUrl}
                    startIcon={<OpenInNew fontSize="extraSmall" />}
                    variant="text"
                    color="inherit"
                    size="extraSmall"
                    sx={{ textTransform: "none", p: 0, minWidth: "auto" }}
                  >
                    {splitCampaign.name || `Campaign ${splitCampaignId}`}
                  </Button>
                </Tooltip>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  {splitCampaign.name || `Campaign ${splitCampaignId}`}
                </Typography>
              )}
              {splitCampaign.url && (
                <Typography variant="body2" color="text.secondary">
                  {splitCampaign.url}
                </Typography>
              )}
            </Stack>
          ) : null}
        </Stack>
      </Stack>
    );
  }

  return (
    <Box py={1} sx={{ display: "flex", alignItems: "center", gap: 1 }}>
      <SmartLinkSelector
        linkType={state.linkType}
        onLinkTypeChange={(type) => {
          onStateChange(entityKey, { linkType: type });
        }}
        selectedLanderId={state.selectedLanderId}
        onLanderChange={(id) => {
          onStateChange(entityKey, { selectedLanderId: id });
        }}
        customUrl={state.customUrl}
        onCustomUrlChange={(url) => {
          onStateChange(entityKey, { customUrl: url });
        }}
        landers={landers}
        campaigns={campaigns}
        selectedCampaignId={state.selectedCampaignId}
        onCampaignChange={(id) => {
          onStateChange(entityKey, { selectedCampaignId: id });
        }}
        disabled={isSaving}
      />
      <Button
        variant="contained"
        size="small"
        onClick={() => {
          if (state.linkType === "lander") {
            onSave(
              entity,
              "lander",
              landerId as any,
              "",
              classificationItemIds,
            );
          } else if (state.linkType === "campaign") {
            onSave(
              entity,
              "campaign",
              campaignIdValue as any,
              "",
              classificationItemIds,
            );
          } else {
            onSave(entity, "url", customUrlValue, null, classificationItemIds);
          }
        }}
        disabled={isSaving}
      >
        {isSaving ? "Saving..." : "Save"}
      </Button>
    </Box>
  );
}
